import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Form, Input, Row, Space } from 'antd';
import Button from '../../../main/components/Button';
import Modal from '../../../main/components/Modal';
import DropzoneWithState from '../../components/DropzoneWithState';
import { useFetch } from '../../../main/utils/makeRequest';
import * as Route from '../../support/serverRoutes';
import Select from '../../components/Select';
import SelectEstablishment from '../Selects/SelectEstablishment';
import generatorPassword from '../../../main/utils/generatorPassword';
import passwordValidation from '../../../main/utils/passwordValidation';
import {
  hasAccessAnonymiseUser,
  hasAccessChangeClassroom,
  hasAccessResetPassword,
  hasAccessUndeleteUser,
  hasAccessUpdateEmail,
  hasAccessUpdateEstablishment,
  hasAccessUpdateFirstName,
  hasAccessUpdateRole,
  hasAccessUpdateSurname,
  hasAccessUpdateUserImage,
  hasDeleteUser,
} from '../../utils/access';
import ConfirmationDeleteModal from './ConfirmationDeleteModal';
import ConfirmationAnonymiseModal from './ConfirmationAnonymiseModal';
import { Divider } from '../../components/Divider';
import SelectMultipleEstablishments from '../Selects/SelectMultipleEstablishments';
import ModalConfirmation from '../../components/ModalConfirmation';
import { USER_ROLES } from '../../utils/factories';

const ModalUpdateAdminAndLecturer = ({
  visible,
  userId,
  onClose,
  locales,
  param,
  isAdminLecturerModal,
  reload = () => {},
  showSoftDeletedUsers = false,
}) => {
  if (!userId) return <></>;

  const [data, loading] = useFetch({ url: Route.users(userId) });
  // Anonymised users are frozen server-side too; soft-deleted ones until recovered
  const isFrozen =
    data?.attributes?.softDeleted === true ||
    data?.attributes?.anonymised === true;
  const [currentRoleOption, setCurrentRoleOption] = useState({
    label: '',
    value: '',
  });
  const [defaultEstablishmentOption, setDefaultEstablishmentOption] =
    useState(null);
  const [selectedEstablishmentId, setSelectedEstablishmentId] = useState(null);
  const [currentClassroomValue, setCurrentClassroomValue] = useState({});
  const [confirmationDeleteVisible, setConfirmationDeleteVisible] =
    useState(false);
  const [confirmationAnonymiseVisible, setConfirmationAnonymiseVisible] =
    useState(false);
  const [avatar, setAvatar] = useState(undefined);

  const [form] = Form.useForm();

  const [establishmentOptions, setEstablishmentOptions] = useState([]);
  const [isConfirmRoleChangeModalOpen, setIsConfirmRoleChangeModalOpen] =
    useState(false);
  const [temporaryRoleOption, setTemporaryRoleOption] = useState(null);
  const [originalEstablishmentName, setOriginalEstablishmentName] =
    useState(null);
  const {
    users: { uploading, deleteLoading },
    user,
    classrooms,
    establishments: {
      data: establishmentList,
      loading: establishmentOptionsLoading,
      userEstablishments,
    },
    adminLecturers: { data: adminLecturersList },
  } = useSelector((state) => state);

  const {
    users: { updateUser, deleteUser, updateImage, recoverUser, anonymiseUser },
    classrooms: {
      fetchParentClassroomsByUser,
      fetchOptionsByUser,
      fetchOptionsByEstablishment,
    },
    blocks: { reload: blocksReload },
    establishments: {
      addEstablishment,
      removeEstablishment,
      setUserEstablishments,
    },
    adminLecturers: { fetchAdminLecturers },
  } = useDispatch();

  useEffect(() => {
    if (!uploading) fetchAdminLecturers(param);
  }, [uploading]);

  useEffect(() => {
    if (adminLecturersList) {
      Object.keys(adminLecturersList).forEach((key) => {
        if (adminLecturersList[key].id === userId)
          setAvatar(adminLecturersList[key].image);
      });
    }
  }, [adminLecturersList]);

  const handleImageChange = (file) => {
    updateImage({
      id: userId,
      image: file,
    });
  };

  const handleChangeRole = (selectedOption) => {
    const foundCurrentRoleOption = USER_ROLES.find(
      (option) => option.value === selectedOption.value,
    );

    // if the user is an admin and the role we are changing to is lecturer or student then we need to ask the user whether they are sure or not via a modal
    if (
      currentRoleOption.value === 'admin' &&
      (foundCurrentRoleOption.value === 'lecturer' ||
        foundCurrentRoleOption.value === 'student')
    ) {
      setTemporaryRoleOption(foundCurrentRoleOption);
      setIsConfirmRoleChangeModalOpen(true);
    } else {
      setCurrentRoleOption(foundCurrentRoleOption);
    }
  };

  const handleConfirmRoleChange = () => {
    setCurrentRoleOption(temporaryRoleOption);
    setIsConfirmRoleChangeModalOpen(false);
  };

  useEffect(() => {
    if (establishmentList) {
      setEstablishmentOptions(establishmentList);
    }
  }, [establishmentList]);

  useEffect(() => {
    if (
      data?.attributes?.establishment?.name &&
      originalEstablishmentName == null
    ) {
      setOriginalEstablishmentName(data.attributes.establishment.name);
    }
  }, [userEstablishments, data]);

  const handleSelect = (establishmentId) => {
    addEstablishment({ establishmentId, userId });
  };

  const handleDelete = (establishmentId) => {
    removeEstablishment({ establishmentId, userId });
  };

  const handleFinish = (values) => {
    const userUpdateArgs = {
      id: userId,
      fields: values,
      setDefaultPassword: values.setDefaultPassword,
    };
    if (selectedEstablishmentId)
      userUpdateArgs.fields.establishmentId = selectedEstablishmentId;
    if (currentRoleOption) userUpdateArgs.fields.role = currentRoleOption.value;
    userUpdateArgs.fields.establishmentId = selectedEstablishmentId;
    if (currentClassroomValue.id)
      userUpdateArgs.classroomId = Number(currentClassroomValue.id);
    if (userUpdateArgs.fields.password === '')
      userUpdateArgs.fields.password = undefined;
    if (
      currentRoleOption.value === 'admin' ||
      currentRoleOption.value === 'super_admin'
    ) {
      userUpdateArgs.establishmentIds = userEstablishments.map((e) => e.id);
    }
    updateUser({
      ...userUpdateArgs,
      showSoftDeletedUsers,
      afterSuccess: () => {
        reload();
        blocksReload();
        onClose();
      },
    });
  };

  const handleChangeEstablishment = ([id]) => {
    if (data?.attributes?.establishment?.id === id) {
      fetchOptionsByUser({ userId, showSoftDeletedUsers });
    } else if (id) {
      fetchOptionsByEstablishment({ establishmentId: id });
    }
    setSelectedEstablishmentId(id);
  };

  useEffect(() => {
    if (data) {
      const { attributes } = data;
      const foundCurrentRoleOption = USER_ROLES.find(
        (option) => option.value === attributes.role,
      );
      if (foundCurrentRoleOption) {
        setCurrentRoleOption(foundCurrentRoleOption);
      }

      setUserEstablishments({ userEstablishments: attributes.establishments });
      if (attributes?.establishment)
        setDefaultEstablishmentOption({
          value: attributes.establishment.name,
          label: attributes.establishment.name,
          id: attributes.establishment.id,
        });
    }
    fetchParentClassroomsByUser({ userId, showSoftDeletedUsers });
    fetchOptionsByUser({ userId, showSoftDeletedUsers });
  }, [data]);

  useEffect(() => {
    if (
      classrooms.options.currentValues &&
      classrooms.options.currentValues[0] != null
    ) {
      setCurrentClassroomValue(classrooms.options.currentValues[0]);
    }
  }, [classrooms]);

  const handleChangeClassroom = (values) => {
    setCurrentClassroomValue(values);
  };

  const setGeneratedPassword = () => {
    form.setFieldsValue({ password: generatorPassword(8) });
  };

  const handleOpenConfirmationModal = () => setConfirmationDeleteVisible(true);

  const handleCloseConfirmationModal = () =>
    setConfirmationDeleteVisible(false);

  const handleDeleteUser = (confirmationPassword) => {
    deleteUser({
      userId,
      confirmationPassword,
      afterSuccess: () => {
        reload();
        blocksReload();
        handleCloseConfirmationModal();
        onClose();
      },
    });
  };

  const handleAnonymiseUser = (confirmationPassword) => {
    anonymiseUser({
      userId,
      confirmationPassword,
      afterSuccess: () => {
        reload();
        blocksReload();
        setConfirmationAnonymiseVisible(false);
        onClose();
      },
    });
  };

  const handleRecoverUser = () => {
    recoverUser({
      userId: data.attributes.id,
      afterSuccess: () => {
        reload();
        blocksReload();
        handleCloseConfirmationModal();
        onClose();
      },
    });
  };

  return (
    <>
      {userId && !loading && currentRoleOption && (
        <Modal
          className="sm"
          isOpen={visible}
          title={locales.admin.userSettings}
          onClose={onClose}
        >
          <Form
            layout="vertical"
            onFinish={handleFinish}
            initialValues={data?.attributes}
            form={form}
          >
            <div className="mt-20">
              <Row gutter={40}>
                <Col span={14}>
                  <Row gutter={10}>
                    <Col span={12}>
                      <Form.Item
                        label={locales.admin.adminEducatorDashboard.firstName}
                        className="admin-input-label"
                        name="firstName"
                        rules={[
                          {
                            required: true,
                            message:
                              locales.admin.adminEducatorDashboard
                                .firstNameIsRequired,
                          },
                        ]}
                      >
                        <Input
                          placeholder={locales.admin.addUserName}
                          className="admin-input"
                          disabled={!hasAccessUpdateFirstName(user) || isFrozen}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label={locales.admin.adminEducatorDashboard.surname}
                        className="admin-input-label"
                        name="surname"
                        rules={[
                          {
                            required: true,
                            message:
                              locales.admin.adminEducatorDashboard
                                .surnameIsRequired,
                          },
                        ]}
                      >
                        <Input
                          placeholder={locales.admin.addUserSurname}
                          className="admin-input"
                          disabled={!hasAccessUpdateSurname(user) || isFrozen}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={10}>
                    <Col span={24}>
                      <Form.Item
                        label={locales.admin.adminEducatorDashboard.email}
                        className="admin-input-label"
                        name="email"
                        rules={
                          data?.attributes?.ssouserId
                            ? []
                            : [
                                {
                                  required: true,
                                  type: 'email',
                                  message:
                                    locales.admin.adminEducatorDashboard
                                      .emailIsRequired,
                                },
                              ]
                        }
                      >
                        <Input
                          className="admin-input"
                          disabled={
                            !hasAccessUpdateEmail(user) ||
                            isFrozen ||
                            data?.attributes?.ssouserId
                          }
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={10}>
                    <Col span={10}>
                      <Form.Item
                        label={locales.admin.adminEducatorDashboard.password}
                        className="admin-input-label"
                        name="password"
                        disabled={!hasAccessResetPassword(user)}
                        rules={[
                          {
                            validator: passwordValidation,
                          },
                        ]}
                      >
                        <Input
                          className="admin-input"
                          disabled={!hasAccessResetPassword(user) || isFrozen}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={14}>
                      <div className="antd-form-item-margin-top">
                        <Button
                          type="button"
                          onClick={() => {
                            setGeneratedPassword();
                          }}
                          disabled={!hasAccessResetPassword(user) || isFrozen}
                        >
                          {locales.admin.generatePassword}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col span={24}>
                      {(currentRoleOption.value === 'lecturer' ||
                        currentRoleOption.value === 'student') && (
                        <Form.Item
                          label={locales.admin.establishment}
                          className="admin-input-label"
                          disabled={!hasAccessUpdateEstablishment(user)}
                        >
                          <SelectEstablishment
                            defaultOption={defaultEstablishmentOption}
                            menuPlacement="top"
                            onChange={handleChangeEstablishment}
                            disabled={
                              !hasAccessUpdateEstablishment(user) || isFrozen
                            }
                          />
                        </Form.Item>
                      )}
                      {currentRoleOption.value === 'admin' && (
                        <SelectMultipleEstablishments
                          establishments={establishmentOptions}
                          loading={uploading || establishmentOptionsLoading}
                          labelOfSelect={
                            locales.admin.inviteUsersToEstablishment
                          }
                          placeholderOfSelect={
                            locales.admin.chooseEstablishmentByName
                          }
                          labelOfList={locales.admin.establishments}
                          selectedIds={userEstablishments.map(
                            (establishment) => establishment.id,
                          )}
                          onSelect={handleSelect}
                          onDelete={handleDelete}
                          disabled={isFrozen}
                        />
                      )}
                    </Col>
                  </Row>
                  <Row>
                    {hasAccessUndeleteUser(user) &&
                      data?.attributes?.softDeleted === true &&
                      data?.attributes?.anonymised !== true && (
                        <Col>
                          <Button
                            type="button"
                            variant="destructive"
                            disabled={deleteLoading}
                            onClick={handleRecoverUser}
                          >
                            {locales.admin.recoverUser}
                          </Button>
                        </Col>
                      )}
                  </Row>
                </Col>
                <Col span={10}>
                  <Row>
                    <Form.Item
                      label={locales.admin.avatar}
                      className="admin-input-label"
                      name="file"
                      disabled={!hasAccessUpdateUserImage(user)}
                    >
                      <div className="dropzone-size">
                        <DropzoneWithState
                          onChange={handleImageChange}
                          locales={locales}
                          initUrl={
                            avatar
                              ? avatar.url
                              : data?.attributes?.image?.original
                          }
                          disabled={!hasAccessUpdateUserImage(user) || isFrozen}
                        />
                      </div>
                    </Form.Item>
                  </Row>
                  <Row>
                    <Col span={24}>
                      <Form.Item
                        label={locales.admin.adminEducatorDashboard.role}
                        className="admin-input-label"
                      >
                        <Select
                          options={USER_ROLES}
                          value={currentRoleOption}
                          onChange={handleChangeRole}
                          disabled={!hasAccessUpdateRole(user) || isFrozen}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row>
                    <Col span={24}>
                      <Form.Item
                        label={locales.admin.class}
                        className="admin-input-label"
                      >
                        <Select
                          options={classrooms.options.collection}
                          value={currentClassroomValue}
                          onChange={handleChangeClassroom}
                          disabled={
                            !hasAccessChangeClassroom(user) ||
                            currentRoleOption.value !== 'student' ||
                            isFrozen
                          }
                          menuPlacement="top"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <div className="mt-20">
                <Divider />
              </div>
              <div className="mt-20">
                {data?.attributes?.anonymised === true && (
                  <Row>
                    <Col span={24}>
                      <p className="text-red-500">
                        {locales.admin.userIsAnonymised.replace(
                          '%{date}',
                          moment(new Date(data.attributes.anonymisedAt)).format(
                            'DD MMM YYYY',
                          ),
                        )}
                      </p>
                    </Col>
                  </Row>
                )}
                <Row justify="space-between" align="middle">
                  {/* Destructive actions grouped on the left, Delete first, like the class and establishment modals */}
                  <Col>
                    <Space>
                      {hasDeleteUser(data, user) && (
                        <Button
                          type="button"
                          variant="destructiveOutline"
                          disabled={
                            deleteLoading ||
                            data?.attributes?.softDeleted === true
                          }
                          onClick={handleOpenConfirmationModal}
                        >
                          {locales.common.deleteText}
                        </Button>
                      )}
                      {isAdminLecturerModal && (
                        <Button
                          type="button"
                          variant="destructiveOutline"
                          onClick={handleOpenConfirmationModal || isFrozen}
                        >
                          {locales.common.deleteText}
                        </Button>
                      )}
                      {hasAccessAnonymiseUser(user) && (
                        <Button
                          type="button"
                          variant="destructiveOutline"
                          disabled={
                            deleteLoading ||
                            data?.attributes?.anonymised === true
                          }
                          onClick={() => setConfirmationAnonymiseVisible(true)}
                        >
                          {locales.admin.anonymiseUser}
                        </Button>
                      )}
                    </Space>
                  </Col>
                  <Col>
                    <Space>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={onClose}
                        disabled={uploading || deleteLoading}
                      >
                        {locales.common.cancel}
                      </Button>
                      <Button
                        type="submit"
                        disabled={deleteLoading || isFrozen || uploading}
                      >
                        {locales.common.save}
                      </Button>
                    </Space>
                  </Col>
                </Row>
              </div>
            </div>
          </Form>
          {/* Rendered inside the settings dialog so headlessui treats it as nested and keeps focus in it */}
          <ConfirmationAnonymiseModal
            loading={deleteLoading}
            visible={confirmationAnonymiseVisible}
            onClose={() => setConfirmationAnonymiseVisible(false)}
            onConfirm={handleAnonymiseUser}
          />
        </Modal>
      )}
      {/* Delete User Modal */}
      <ConfirmationDeleteModal
        loading={deleteLoading}
        visible={confirmationDeleteVisible}
        onClose={handleCloseConfirmationModal}
        onConfirm={handleDeleteUser}
      />
      {/* Confirm Role Change Modal */}
      <ModalConfirmation
        locales={locales}
        visible={isConfirmRoleChangeModalOpen}
        onClose={() => setIsConfirmRoleChangeModalOpen(false)}
        onConfirm={() => {
          handleConfirmRoleChange();
        }}
        bodyText={locales.admin.adminEducatorDashboard.confirmRoleChangeBody.replace(
          '%{originalEstablishmentName}',
          originalEstablishmentName,
        )}
        confirmationTitle={
          locales.admin.adminEducatorDashboard.confirmRoleChange
        }
      />
    </>
  );
};

ModalUpdateAdminAndLecturer.propTypes = {
  onClose: PropTypes.func.isRequired,
  userId: PropTypes.number,
};
export default ModalUpdateAdminAndLecturer;
